import { useTranslation } from "react-i18next";

import { Skeleton } from "~/components/ui";
import { cn } from "~/lib/utils";

interface LoadingProps {
  className?: string;
}

const ROWS = ["r1", "r2", "r3", "r4", "r5", "r6"];
const FIELDS = ["f1", "f2", "f3", "f4"];
const LINES = ["l1", "l2", "l3", "l4"];

/**
 * Shimmering fallbacks that replace the old "…" Suspense placeholders. Each mimics
 * the shape of what's loading so the layout doesn't jump when the content arrives.
 */

/** Boxed fallback for a data view / table / tab — a header bar over shimmer rows. */
export function PanelLoading({ className }: LoadingProps) {
  const { t } = useTranslation();
  return (
    <div
      aria-live="polite"
      aria-busy
      className={cn("overflow-hidden rounded-[var(--radius-md)] border border-border-default bg-white", className)}
    >
      <span className="sr-only">{t("ui.loading")}</span>
      <div className="flex items-center gap-4 border-b border-border-default px-5 py-3">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="ml-auto h-3.5 w-16" />
      </div>
      {ROWS.map((row) => (
        <div key={row} className="flex items-center gap-4 border-b border-border-default px-5 py-3 last:border-b-0">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="ml-auto h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Sidebar fallback for the filter form — stacked label + input field groups. */
export function FilterFormLoading({ className }: LoadingProps) {
  const { t } = useTranslation();
  return (
    <div aria-live="polite" aria-busy className={cn("flex flex-col gap-5", className)}>
      <span className="sr-only">{t("ui.loading")}</span>
      {FIELDS.map((field) => (
        <div key={field} className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-8 w-full rounded-[var(--radius-sm)]" />
        </div>
      ))}
      <Skeleton className="h-8 w-24 rounded-[var(--radius-sm)]" />
    </div>
  );
}

/** Generic fallback for detail panels (peeks), dialog forms and inline content —
 *  a title bar over stacked label + value lines. */
export function DetailLoading({ className }: LoadingProps) {
  const { t } = useTranslation();
  return (
    <div aria-live="polite" aria-busy className={cn("flex flex-col gap-4", className)}>
      <span className="sr-only">{t("ui.loading")}</span>
      <Skeleton className="h-4 w-2/5" />
      {LINES.map((line) => (
        <div key={line} className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-3.5 w-full" />
        </div>
      ))}
    </div>
  );
}

/** Full-width route-level fallback — a page header over a panel skeleton. */
export function PageLoading({ className }: LoadingProps) {
  return (
    <div className={cn("mx-auto max-w-[var(--content-max)] space-y-5 p-8", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-8 w-28 rounded-[var(--radius-sm)]" />
      </div>
      <PanelLoading />
    </div>
  );
}
